import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { ArrowLeft, User, QrCode, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  'Active': { bg: 'bg-[#F0FFF4]', text: 'text-[#15803D]', border: 'border-green-200' },
  'Inactive': { bg: 'bg-[#F7F7F7]', text: 'text-[#777]', border: 'border-[#E5E5E5]' },
  'Suspended': { bg: 'bg-[#FEF2F2]', text: 'text-[#D71920]', border: 'border-red-200' },
  'Deceased': { bg: 'bg-black/5', text: 'text-black/60', border: 'border-black/20' },
  'Transferred': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
};

const MEMBER_STATUSES = ['Active', 'Inactive', 'Suspended', 'Deceased', 'Transferred'];

const InfoRow = ({ label, value }) => (
  <div className="py-3 border-b border-[#F7F7F7] last:border-0">
    <p className="text-xs text-[#999] mb-0.5">{label}</p>
    <p className="text-sm font-semibold text-[#0B0B0B]">{value || '—'}</p>
  </div>
);

const MemberProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [statusRemarks, setStatusRemarks] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchMember(); }, [id]);

  const fetchMember = async () => {
    try {
      const memberRes = await axios.get(`${API_URL}/members/${id}`);
      setMember(memberRes.data);
      setSelectedStatus(memberRes.data.status);
      const schoolRes = await axios.get(`${API_URL}/schools/${memberRes.data.school_id}`);
      setSchool(schoolRes.data);
    } catch { toast.error('Gagal memuatkan data ahli'); } finally { setLoading(false); }
  };

  const handleStatusUpdate = async () => {
    if (!statusRemarks.trim()) { toast.error('Sila isi catatan'); return; }
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/members/${id}/status`, { status: selectedStatus, remarks: statusRemarks });
      toast.success('Status dikemaskini');
      fetchMember();
      setStatusRemarks('');
    } catch { toast.error('Gagal mengemaskini status'); } finally { setUpdating(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== 'PADAM') { toast.error('Sila taip "PADAM"'); return; }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/members/${id}`, { data: { confirmation: deleteConfirmation } });
      toast.success('Ahli dipadam');
      navigate('/admin/members');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memadam');
    } finally { setDeleting(false); setDeleteDialogOpen(false); setDeleteConfirmation(''); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#999] animate-pulse">Memuatkan...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!member) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-sm text-[#777]">Ahli tidak dijumpai</p>
        </div>
      </AdminLayout>
    );
  }

  const sc = statusConfig[member.status] || statusConfig['Inactive'];
  const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/verify?token=${member.qr_token}`;

  return (
    <AdminLayout>
      <div className="space-y-5 max-w-5xl">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/members')}
            className="p-2 rounded border border-[#E5E5E5] text-[#555] hover:bg-[#F7F7F7] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#0B0B0B]" data-testid="member-name">{member.full_name}</h1>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${sc.bg} ${sc.text} ${sc.border}`}>{member.status}</span>
              {member.is_deleted && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-black/10 text-black/60 border border-black/20"><Trash2 size={9} className="inline mr-0.5" />Dipadam</span>}
            </div>
            <p className="font-mono text-xs text-[#D71920] font-bold mt-0.5">{member.member_no}</p>
          </div>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">

          {/* Photo + QR */}
          <div className="bg-white rounded border border-[#E5E5E5] p-5 flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-[#D71920]/8 border-2 border-[#D71920]/20 flex items-center justify-center mb-4">
              {member.photo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${member.photo_url}`} alt={member.full_name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <User size={28} className="text-[#D71920]" />
              )}
            </div>
            <div className="text-center">
              <p className="font-mono text-xs text-[#D71920] font-bold">{member.member_no}</p>
              <p className="font-bold text-sm text-[#0B0B0B] mt-1">{member.full_name}</p>
              <p className="text-xs text-[#777] mt-0.5">{school?.name}</p>
            </div>
            <button
              onClick={() => window.open(qrUrl, '_blank')}
              className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-[#555] border border-[#E5E5E5] hover:border-[#D71920]/30 hover:text-[#D71920] px-3 py-2 rounded transition-colors"
              data-testid="qr-button"
            >
              <QrCode size={13} /> Kod QR
            </button>
          </div>

          {/* Maklumat Peribadi */}
          <div className="bg-white rounded border border-[#E5E5E5] p-5">
            <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-3">Maklumat Peribadi</div>
            <InfoRow label="No. IC (Tertutup)" value={member.ic_masked} />
            <InfoRow label="Jantina" value={member.gender} />
            <InfoRow label="No. Telefon" value={member.phone} />
            <InfoRow label="Emel" value={member.email} />
            <InfoRow label="Perguruan" value={school?.name} />
          </div>

          {/* Kemaskini Status */}
          {!member.is_deleted && (
            <div className="bg-white rounded border border-[#E5E5E5] p-5">
              <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-3">Kemaskini Status</div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-[#777] mb-1.5">Status Ahli</p>
                  <div className="flex flex-wrap gap-1.5">
                    {MEMBER_STATUSES.map(s => (
                      <button
                        key={s}
                        onClick={() => setSelectedStatus(s)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                          selectedStatus === s
                            ? 'bg-[#0B0B0B] text-white border-[#0B0B0B]'
                            : 'bg-white text-[#555] border-[#E5E5E5] hover:border-[#0B0B0B]'
                        }`}
                        data-testid={`status-option-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[#777] mb-1.5">Catatan *</p>
                  <Input
                    value={statusRemarks}
                    onChange={e => setStatusRemarks(e.target.value)}
                    placeholder="Sebab kemaskini status..."
                    className="border-[#E5E5E5] text-sm"
                    data-testid="remarks-input"
                  />
                </div>
                <button
                  onClick={handleStatusUpdate}
                  disabled={updating || !statusRemarks.trim() || selectedStatus === member.status}
                  className="w-full bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold py-2.5 rounded transition-colors disabled:opacity-50"
                  data-testid="update-status-button"
                >
                  {updating ? 'Mengemaskini...' : 'Kemaskini Status'}
                </button>

                <div className="pt-3 border-t border-[#F7F7F7]">
                  <button
                    onClick={() => setDeleteDialogOpen(true)}
                    className="w-full flex items-center justify-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold py-2 rounded transition-colors"
                    data-testid="delete-button"
                  >
                    <Trash2 size={14} /> Padam Ahli
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-700">Padam Ahli</DialogTitle>
              <DialogDescription>
                Anda akan memadam <strong>{member.full_name}</strong>. Taip <strong className="text-red-700">PADAM</strong> untuk sahkan.
              </DialogDescription>
            </DialogHeader>
            <Input
              value={deleteConfirmation}
              onChange={e => setDeleteConfirmation(e.target.value.toUpperCase())}
              className="border-red-200"
              placeholder="Taip PADAM"
              data-testid="delete-confirmation-input"
            />
            <DialogFooter>
              <button onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmation(''); }} className="px-4 py-2 text-sm border border-[#E5E5E5] rounded hover:bg-[#F7F7F7]">Batal</button>
              <button onClick={handleDelete} disabled={deleteConfirmation !== 'PADAM' || deleting} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50" data-testid="confirm-delete-button">
                {deleting ? 'Memadam...' : 'Padam'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default MemberProfile;
